import {
  AgreeReactionIcon,
  CheckYesIcon,
  ClapReactionIcon,
  DisagreeReactionIcon,
  DislikeReactionIcon,
  HandRaiseIcon,
  LaughReactionIcon,
  LikeReactionIcon,
  LoveReactionIcon,
  WowReactionIcon,
  XNoIcon,
} from "@minervaproject/fds";

import { useCapabilities } from "hooks/capabilities";
import { useAppSelector } from "hooks/redux_hooks";
import { RecordingStateType } from "shared/types";
import { classroomSelectors } from "store";
import { getPlatform } from "utils/get_platform";

import "./keyboard_shortcuts.scss";

function KeyboardShortcut({
  description,
  keys,
  IconComponent,
}: {
  description: string;
  keys: string[];
  IconComponent?: React.ElementType;
}) {
  const iconProps = {
    width: 20,
    height: 20,
    className: "mr1",
  };
  return (
    <li className="flex items-center justify-between mb2">
      <div className="flex items-center">
        {IconComponent && <IconComponent {...iconProps} />}
        <span className="body-s text-black-tint-40">{description}</span>
      </div>
      <div>
        {keys.map((key) => (
          <div key={key} className="key body-s bold">
            {key}
          </div>
        ))}
      </div>
    </li>
  );
}
function KeyboardShortcuts() {
  const { canControlClassroom, canParticipateInClass, isVisibleInClass } = useCapabilities();
  const isAvEnabled = useAppSelector(classroomSelectors.selectCurrentUserIsAvEnabled());
  const optionOrAlt = getPlatform().includes("Windows") ? "Alt" : "Option";
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const recordingState = useAppSelector(classroomSelectors.selectRecordingState());
  const isRecording = recordingState === RecordingStateType.Recording;

  return (
    <div className="flex">
      <div className={isVisibleInClass || canControlClassroom ? "col-6 pr6" : ""}>
        {isVisibleInClass && (
          <>
            <div className="body-s bold mb2">Signals & Reactions</div>
            <ul>
              <KeyboardShortcut
                description="Hand Raise"
                keys={["H"]}
                IconComponent={HandRaiseIcon}
              />
              <KeyboardShortcut description="Vote No" keys={["N"]} IconComponent={XNoIcon} />
              <KeyboardShortcut description="Vote Yes" keys={["Y"]} IconComponent={CheckYesIcon} />
              <KeyboardShortcut description="Reactions Overlay" keys={["R"]} />
              <KeyboardShortcut
                description="Like"
                keys={["SHIFT", "1"]}
                IconComponent={LikeReactionIcon}
              />
              <KeyboardShortcut
                description="Dislike"
                keys={["SHIFT", "2"]}
                IconComponent={DislikeReactionIcon}
              />
              <KeyboardShortcut
                description="Agree"
                keys={["SHIFT", "3"]}
                IconComponent={AgreeReactionIcon}
              />
              <KeyboardShortcut
                description="Disagree"
                keys={["SHIFT", "4"]}
                IconComponent={DisagreeReactionIcon}
              />
              <KeyboardShortcut
                description="Laugh"
                keys={["SHIFT", "5"]}
                IconComponent={LaughReactionIcon}
              />
              <KeyboardShortcut
                description="Wow"
                keys={["SHIFT", "6"]}
                IconComponent={WowReactionIcon}
              />
              <KeyboardShortcut
                description="Love"
                keys={["SHIFT", "7"]}
                IconComponent={LoveReactionIcon}
              />
              <KeyboardShortcut
                description="Clap"
                keys={["SHIFT", "8"]}
                IconComponent={ClapReactionIcon}
              />
            </ul>
          </>
        )}

        {canControlClassroom && (
          <>
            <div className="body-s bold mt5 mb2">Timeline - Instructor</div>
            <ul>
              <KeyboardShortcut description="Next Timeline Segment" keys={["."]} />
              <KeyboardShortcut description="Previous Timeline Segment" keys={[","]} />
              <KeyboardShortcut description="Toggle Instructor Notes" keys={["P"]} />
              <KeyboardShortcut description="Toggle Timeline" keys={["/"]} />
            </ul>
          </>
        )}
      </div>
      <div className="col-6">
        <div className="body-s bold mb2">Classroom</div>
        <ul>
          {isRecording && !activeBreakoutSessionId && (
            <KeyboardShortcut description="Bookmark This Moment" keys={["B"]} />
          )}
          <KeyboardShortcut description="Chat" keys={["C"]} />
          <KeyboardShortcut description="Directory" keys={["D"]} />
          <KeyboardShortcut description="Close Sidebar Panel" keys={["ESC"]} />
          <KeyboardShortcut description="Toggle Keyboard Shortcuts" keys={["K"]} />
          {isAvEnabled && isVisibleInClass && (
            <>
              <KeyboardShortcut description="Speak" keys={["CTRL"]} />
              <KeyboardShortcut description="Toggle Mute" keys={["M"]} />
            </>
          )}
        </ul>
        {canParticipateInClass && (
          <>
            <div className="body-s bold mt5 mb2">Breakouts</div>
            <ul>
              <KeyboardShortcut description="n-Up View" keys={["1 - 8"]} />
            </ul>
          </>
        )}
        {canControlClassroom && (
          <>
            <div className="body-s bold mt5 mb2">Classroom - Instructor</div>
            <ul>
              <KeyboardShortcut description="Clear All Signals" keys={["S"]} />
              <KeyboardShortcut description="Clear Stage" keys={["E"]} />
              <KeyboardShortcut description="Confetti Cannon" keys={["`"]} />
              <KeyboardShortcut description="n-Up View" keys={["1 - 8"]} />
              <KeyboardShortcut description="Toggle Absent Students" keys={["A"]} />
              <KeyboardShortcut description="Timer" keys={["I"]} />
              <KeyboardShortcut description="Detailed Talk Time" keys={[optionOrAlt, "T"]} />
              <KeyboardShortcut description="Talk Time" keys={["T"]} />
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

export { KeyboardShortcut, KeyboardShortcuts };
